import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Edit2, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const emptyUser = {
    email: '',
    name: '',
    role: 'editor',
    password: '',
    is_active: true,
};

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
    const [pwdTarget, setPwdTarget] = useState(null);
    const [newPwd, setNewPwd] = useState('');

    const isAdmin = me?.role === 'admin';

    const load = () => {
        setLoading(true);
        api.get('/admin/users').then(({ data }) => setUsers(data))
            .catch((e) => { if (e?.response?.status === 403) toast.error('Samo admin može upravljati korisnicima.'); })
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (!isAdmin) {
        return (
            <div>
                <h1 className="text-2xl font-medium">Korisnici</h1>
                <Card className="mt-6 bg-[#111318] border-[#232A36] p-6">
                    <p className="text-white/70 text-sm">Pristup ovoj sekciji ima samo korisnik s ulogom <b>admin</b>.</p>
                </Card>
            </div>
        );
    }

    const open = (u) => {
        setEditing(u ? { ...u, password: '' } : { ...emptyUser });
        setDialogOpen(true);
    };
    const close = () => { setDialogOpen(false); setEditing(null); };

    const onSave = async () => {
        setSaving(true);
        try {
            if (editing.id) {
                // Update (no password here - separate endpoint)
                await api.put(`/admin/users/${editing.id}`, {
                    email: editing.email,
                    name: editing.name,
                    role: editing.role,
                    is_active: editing.is_active,
                });
                toast.success('Korisnik ažuriran');
            } else {
                if (!editing.password || editing.password.length < 6) {
                    toast.error('Lozinka mora imati najmanje 6 znakova');
                    setSaving(false);
                    return;
                }
                await api.post('/admin/users', editing);
                toast.success('Korisnik kreiran');
            }
            close();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (u) => {
        if (!confirm(`Obrisati korisnika "${u.email}"?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success('Obrisano');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Brisanje nije uspjelo');
        }
    };

    const onToggleActive = async (u) => {
        try {
            await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
            toast.success(!u.is_active ? 'Aktivirano' : 'Deaktivirano');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Promjena nije uspjela');
        }
    };

    const openPwd = (u) => { setPwdTarget(u); setNewPwd(''); setPwdDialogOpen(true); };
    const submitPwd = async () => {
        if (!newPwd || newPwd.length < 6) { toast.error('Lozinka mora imati najmanje 6 znakova'); return; }
        try {
            await api.post(`/admin/users/${pwdTarget.id}/password`, { new_password: newPwd });
            toast.success('Lozinka postavljena');
            setPwdDialogOpen(false);
            setNewPwd('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Promjena nije uspjela');
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Korisnici</h1>
                    <p className="text-sm text-white/60 mt-1">Admin upravljanje računima i ulogama.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white" data-testid="admin-users-new">
                    <Plus className="mr-2 h-4 w-4" /> Novi korisnik
                </Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Ime</th>
                            <th className="text-left px-4 py-3">Uloga</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">Kreiran</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="p-8 text-center text-white/60">Učitavanje…</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan="6" className="p-8 text-center text-white/60">Nema korisnika.</td></tr>
                        ) : users.map((u) => (
                            <tr key={u.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 font-mono text-xs">{u.email}{u.id === me?.id && <span className="ml-2 text-[10px] text-[#B08D57]">(ti)</span>}</td>
                                <td className="px-4 py-3">{u.name || '—'}</td>
                                <td className="px-4 py-3">
                                    <Badge variant="outline" className={u.role === 'admin' ? 'border-[#B08D57] text-[#B08D57]' : 'border-blue-700 text-blue-400'}>
                                        {u.role}
                                    </Badge>
                                </td>
                                <td className="px-4 py-3">
                                    <Badge variant="outline" className={u.is_active ? 'border-green-700 text-green-400' : 'border-yellow-700 text-yellow-400'}>
                                        {u.is_active ? 'Aktivan' : 'Deaktiviran'}
                                    </Badge>
                                </td>
                                <td className="px-4 py-3 text-white/60 text-xs">{formatDate(u.created_at)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2">
                                        <Button size="sm" variant="outline" onClick={() => openPwd(u)} title="Postavi lozinku" className="border-[#232A36] text-white hover:bg-[#232A36]"><KeyRound className="h-3.5 w-3.5" /></Button>
                                        <Button size="sm" variant="outline" onClick={() => open(u)} title="Uredi" className="border-[#232A36] text-white hover:bg-[#232A36]"><Edit2 className="h-3.5 w-3.5" /></Button>
                                        {u.id !== me?.id && (
                                            <Button size="sm" variant="outline" onClick={() => onToggleActive(u)} title={u.is_active ? 'Deaktiviraj' : 'Aktiviraj'} className="border-[#232A36] text-white hover:bg-[#232A36] text-xs px-2 h-8">
                                                {u.is_active ? 'Deakt.' : 'Akt.'}
                                            </Button>
                                        )}
                                        {u.id !== me?.id && (
                                            <Button size="sm" variant="outline" onClick={() => onDelete(u)} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {/* Create/Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg bg-[#111318] border-[#232A36] text-white">
                    <DialogHeader><DialogTitle>{editing?.id ? 'Uredi korisnika' : 'Novi korisnik'}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={editing.email} onChange={(e) => setEditing({...editing, email: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                            </div>
                            <div>
                                <Label>Ime (opcionalno)</Label>
                                <Input value={editing.name || ''} onChange={(e) => setEditing({...editing, name: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                            </div>
                            <div>
                                <Label>Uloga</Label>
                                <Select value={editing.role} onValueChange={(v) => setEditing({...editing, role: v})} disabled={editing.id === me?.id}>
                                    <SelectTrigger className="mt-2 bg-[#0B0C0E] border-[#232A36]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">admin (puni pristup)</SelectItem>
                                        <SelectItem value="editor">editor (sadržaj samo)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {editing.id === me?.id && <p className="text-xs text-white/55 mt-1">Ne možete promijeniti vlastitu ulogu.</p>}
                            </div>
                            {!editing.id && (
                                <div>
                                    <Label>Početna lozinka *</Label>
                                    <Input type="password" value={editing.password} onChange={(e) => setEditing({...editing, password: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                                    <p className="text-xs text-white/55 mt-1">Min. 6 znakova. Korisnik je može kasnije sam promijeniti.</p>
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({...editing, is_active: v})} disabled={editing.id === me?.id} />
                                <Label>Aktivan</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Spremi'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Set password dialog */}
            <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
                <DialogContent className="max-w-md bg-[#111318] border-[#232A36] text-white">
                    <DialogHeader><DialogTitle>Postavi novu lozinku</DialogTitle></DialogHeader>
                    {pwdTarget && (
                        <div className="space-y-4">
                            <p className="text-sm text-white/70">Korisnik: <span className="font-mono">{pwdTarget.email}</span></p>
                            <div>
                                <Label>Nova lozinka</Label>
                                <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" />
                                <p className="text-xs text-white/55 mt-1">Min. 6 znakova. Lozinka se nigdje ne prikazuje, samo se sprema (hash).</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwdDialogOpen(false)} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={submitPwd} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">Postavi</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
